import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Home, BookOpen, Mic, Video, BookMarked, Heart, Bookmark,
  MoreHorizontal, NotebookPen, Compass, LogIn, LogOut, Shield, Flame,
  Sparkles,
} from "lucide-react";
import WhatsAppButton from "./WhatsAppButton";
import MigrationModal from "./MigrationModal";
import OfflineBanner from "./OfflineBanner";
import AnonymousSignupBanner from "./AnonymousSignupBanner";
import { useAuth } from "../lib/authContext";
import { getCurrentStreak } from "../lib/dailyJourney";

const NAV = [
  { to: "/", label: "Início", icon: Home, testid: "nav-home" },
  { to: "/devocional", label: "Devocional", icon: BookOpen, testid: "nav-devocional" },
  { to: "/sermoes", label: "Sermões", icon: Mic, testid: "nav-sermoes" },
  { to: "/videos", label: "Vídeos", icon: Video, testid: "nav-videos" },
  { to: "/livros", label: "Livros", icon: BookMarked, testid: "nav-livros" },
];

function HeaderMenu() {
  const { user, isAuthenticated, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const close = () => setOpen(false);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Menu"
        aria-expanded={open}
        data-testid="header-menu-button"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/80 hover:text-gold transition-colors"
      >
        {isAuthenticated && user?.name ? (
          <span className="hidden sm:inline normal-case tracking-normal text-white/85">
            Olá, <span className="text-gold">{user.name}</span>
          </span>
        ) : null}
        <MoreHorizontal className="w-5 h-5" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-60 rounded-2xl bg-white border border-border shadow-xl overflow-hidden z-50"
          data-testid="header-menu-panel"
        >
          {isAuthenticated && (
            <div className="px-4 pt-3 pb-2 border-b border-border bg-[hsl(var(--parchment))]">
              <p className="text-[10px] uppercase tracking-[0.3em] text-navy/55">Logado como</p>
              <p className="font-display text-sm text-navy mt-0.5 truncate">{user?.email}</p>
            </div>
          )}

          <Link to="/oracao" onClick={close} data-testid="menu-item-oracao"
            className="flex items-center gap-3 px-4 py-3 text-sm text-navy/80 hover:bg-[hsl(var(--parchment))] hover:text-navy">
            <Heart className="w-4 h-4 text-gold" /> Pedido de Oração
          </Link>
          <Link to="/assistente" onClick={close} data-testid="menu-item-assistente"
            className="flex items-center gap-3 px-4 py-3 text-sm text-navy/80 hover:bg-[hsl(var(--parchment))] hover:text-navy">
            <Sparkles className="w-4 h-4 text-gold" /> Auxiliar Bíblico
          </Link>
          <Link to="/comunidade" onClick={close} data-testid="menu-item-comunidade"
            className="flex items-center gap-3 px-4 py-3 text-sm text-navy/80 hover:bg-[hsl(var(--parchment))] hover:text-navy">
            <Compass className="w-4 h-4 text-gold" /> Comunidade
          </Link>

          {/* Recursos PESSOAIS — somente para usuários autenticados
              (Fase A/iter64: separação estrita público × logado). */}
          {isAuthenticated && (
            <>
              <Link to="/jornada" onClick={close} data-testid="menu-item-jornada"
                className="flex items-center gap-3 px-4 py-3 text-sm text-navy/80 hover:bg-[hsl(var(--parchment))] hover:text-navy">
                <Flame className="w-4 h-4 text-gold" /> Sua jornada
              </Link>
              <Link to="/diario" onClick={close} data-testid="menu-item-diario"
                className="flex items-center gap-3 px-4 py-3 text-sm text-navy/80 hover:bg-[hsl(var(--parchment))] hover:text-navy">
                <NotebookPen className="w-4 h-4 text-gold" /> Meu Diário
              </Link>
              <Link to="/historico" onClick={close} data-testid="menu-item-historico"
                className="flex items-center gap-3 px-4 py-3 text-sm text-navy/80 hover:bg-[hsl(var(--parchment))] hover:text-navy">
                <NotebookPen className="w-4 h-4 text-gold" /> Histórico de leitura
              </Link>
            </>
          )}

          {isAuthenticated && user?.role === "admin" && (
            <Link to="/admin" onClick={close} data-testid="menu-item-admin"
              className="flex items-center gap-3 px-4 py-3 text-sm text-navy/80 hover:bg-[hsl(var(--parchment))] hover:text-navy border-t border-border bg-[hsl(var(--parchment))]/40">
              <Shield className="w-4 h-4 text-gold" /> Painel Admin
            </Link>
          )}

          <div className="border-t border-border" />

          {isAuthenticated ? (
            <button
              type="button"
              onClick={() => { close(); logout(); }}
              data-testid="menu-item-logout"
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-navy/80 hover:bg-[hsl(var(--parchment))] hover:text-navy text-left"
            >
              <LogOut className="w-4 h-4 text-gold" /> Sair
            </button>
          ) : (
            <Link to="/entrar" onClick={close} data-testid="menu-item-entrar"
              className="flex items-center gap-3 px-4 py-3 text-sm text-navy/80 hover:bg-[hsl(var(--parchment))] hover:text-navy">
              <LogIn className="w-4 h-4 text-gold" /> Entrar / Criar conta
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function StreakBadge() {
  const { isAuthenticated } = useAuth();
  const [streak, setStreak] = React.useState(() => getCurrentStreak());
  const location = useLocation();
  // Re-read when route changes (after marking a chapter as read elsewhere).
  React.useEffect(() => {
    setStreak(getCurrentStreak());
  }, [location.pathname]);
  // Fase A/iter64: streak é recurso PESSOAL — somente autenticado.
  if (!isAuthenticated) return null;
  if (streak < 3) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-display text-gold"
      title={`${streak} dias seguidos`}
      data-testid="header-streak"
      data-streak={streak}
    >
      <Flame className="w-4 h-4 fill-gold/30" />
      <span>{streak}</span>
    </span>
  );
}

export default function Layout() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-offwhite flex flex-col" data-testid="app-layout">
      <header className="glass-navy sticky top-0 z-40 border-b border-white/5" data-testid="app-header">
        <div className="max-w-5xl mx-auto section-padding py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 group min-w-0" data-testid="header-logo">
            <img
              src="/icon-light.png"
              alt="C E Medeiros"
              width="36"
              height="36"
              className="w-9 h-9 object-contain shrink-0"
              data-testid="header-logo-icon"
            />
            <div className="leading-tight min-w-0">
              <div className="font-display text-white text-base sm:text-lg truncate">C E Medeiros</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">Conheça o poder da palavra</div>
            </div>
          </Link>

          <div className="flex items-center gap-4 shrink-0">
            <StreakBadge />
            {/* Meus Versículos — recurso pessoal (favoritos), autenticado apenas.
                Fase A/iter64: separação estrita público × logado. */}
            {isAuthenticated && (
              <Link
                to="/favoritos"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/80 hover:text-gold transition-colors"
                data-testid="header-favoritos-link"
                aria-label="Meus versículos"
              >
                <Bookmark className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Meus Versículos</span>
              </Link>
            )}
            {/* Entrar (CTA público) — visível somente para anônimos, direto no header. */}
            {!isAuthenticated && (
              <Link
                to="/entrar"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/80 hover:text-gold transition-colors"
                data-testid="header-entrar-link"
              >
                <LogIn className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Entrar</span>
              </Link>
            )}
            <HeaderMenu />
          </div>
        </div>
      </header>

      <main className="flex-1 pb-28" data-testid="app-main">
        <OfflineBanner />
        <Outlet />
      </main>

      <AnonymousSignupBanner />

      {!isAdmin && (
        <nav
          className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-border"
          data-testid="bottom-nav"
        >
          <div className="max-w-5xl mx-auto grid grid-cols-5">
            {NAV.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] tracking-wide transition-colors ${
                    isActive ? "text-navy" : "text-navy/50 hover:text-navy"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className={`w-5 h-5 ${isActive ? "stroke-[2.2]" : "stroke-[1.6]"}`} />
                    <span className="uppercase">{label}</span>
                    {isActive && <span className="w-1 h-1 rounded-full bg-gold" />}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}

      {!isAdmin && <WhatsAppButton />}
      <MigrationModal />
    </div>
  );
}
